import React from 'react';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ReferenceLine, ResponsiveContainer, CartesianGrid, Cell } from 'recharts';

var API = 'https://api.algrok.com';

function fmtCr(n){if(n==null||isNaN(n))return'--';var a=Math.abs(n);var s=n<0?'-':'';if(a>=1e6)return s+(a/1e6).toFixed(2)+'M Cr';if(a>=1e3)return s+(a/1e3).toFixed(2)+'K Cr';return s+a.toFixed(1)+' Cr';}
function fmtSignedK(n){if(n==null||isNaN(n)||n===0)return'·';var sign=n<0?'-':'+';var a=Math.abs(n);if(a>=1e5)return sign+(a/1e5).toFixed(1)+'L';if(a>=1e3)return sign+(a/1e3).toFixed(0)+'k';return sign+a.toFixed(0);}
function labelMeta(label){
  if(label==='BUILDING_STRONG') return {txt:'B★',fg:'#22c55e'};
  if(label==='BUILDING')        return {txt:'B', fg:'#16a34a'};
  if(label==='UNWINDING_STRONG')return {txt:'U★',fg:'#fb923c'};
  if(label==='UNWINDING')       return {txt:'U', fg:'#fb923c'};
  if(label==='CHURN')           return {txt:'C', fg:'#a78bfa'};
  return {txt:'·',fg:'#6b7280'};
}
function MigMini({mig}){
  if(!mig||mig.open==null) return null;
  var d=mig.delta||0;
  if(d===0) return <span style={{fontSize:8,color:'#6b7280'}}>→0</span>;
  var c=d>0?'#22c55e':'#ef4444';
  return <span style={{fontSize:8,color:c,fontWeight:700}}>{d>0?'↑+':'↓'}{d}</span>;
}

function GammaExposureM(){
  var [state,setState]=useState({});
  var [vrp,setVrp]=useState({});
  var [history,setHistory]=useState([]);
  var [strikeRange,setStrikeRange]=useState(10);
  var [tab,setTab]=useState('chart'); // 'chart' | 'flow'
  var [sliderT,setSliderT]=useState(null);
  var pollRef=useRef(null);

  var fetchState=useCallback(function(){
    if(sliderT) return;
    fetch(API+'/gex/state').then(function(r){return r.json();}).then(function(d){
      if(d && !d.error) setState(d);
    }).catch(function(){});
    fetch(API+'/gex/history').then(function(r){return r.json();}).then(function(d){
      if(d && d.history) setHistory(d.history);
    }).catch(function(){});
    fetch(API+'/vrp/state').then(function(r){return r.json();}).then(function(d){
      if(d && !d.error) setVrp(d);
    }).catch(function(){});
  },[sliderT]);

  useEffect(function(){
    fetchState();
    pollRef.current=setInterval(fetchState,5000);
    return function(){if(pollRef.current)clearInterval(pollRef.current);};
  },[fetchState]);

  useEffect(function(){
    if(!sliderT) return;
    fetch(API+'/gex/snapshot?t='+encodeURIComponent(sliderT)).then(function(r){return r.json();}).then(function(d){
      if(d && !d.error){
        setState(function(prev){return Object.assign({},prev,{
          spot:d.spot, aggregates:d.aggregates,
          per_strike:(d.per_strike_compact||[]).map(function(p){return {strike:p[0],net_gex:p[1]};}),
          last_compute_time:d.time_hhmm,
        });});
      }
    }).catch(function(){});
  },[sliderT]);

  var setExpiry=function(v){
    fetch(API+'/gex/config',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({expiry_choice:v})})
      .then(function(){fetchState();}).catch(function(){});
  };

  var displayStrikes=useMemo(function(){
    var s=state.per_strike||[];
    var spot=state.spot||0;
    if(!s.length||!spot) return [];
    var sorted=s.slice().sort(function(a,b){return a.strike-b.strike;});
    var atmIdx=0;var minDist=Infinity;
    sorted.forEach(function(r,i){var d=Math.abs(r.strike-spot);if(d<minDist){minDist=d;atmIdx=i;}});
    return sorted.slice(Math.max(0,atmIdx-strikeRange),Math.min(sorted.length,atmIdx+strikeRange+1));
  },[state.per_strike,state.spot,strikeRange]);

  var chartData=useMemo(function(){
    return displayStrikes.map(function(r){return {strike:r.strike,net_gex:r.net_gex,abs_gex:Math.abs(r.net_gex||0)};});
  },[displayStrikes]);

  // Flow table — ATM±7 (compact for mobile)
  var flowRows=useMemo(function(){
    var s=state.per_strike||[];
    var spot=state.spot||0;
    if(!s.length||!spot) return [];
    var sorted=s.slice().sort(function(a,b){return a.strike-b.strike;});
    var atmIdx=0;var minDist=Infinity;
    sorted.forEach(function(r,i){var d=Math.abs(r.strike-spot);if(d<minDist){minDist=d;atmIdx=i;}});
    var lo=Math.max(0,atmIdx-7);
    var hi=Math.min(sorted.length,atmIdx+8);
    return sorted.slice(lo,hi).map(function(r){return Object.assign({},r,{_atm:r.strike===sorted[atmIdx].strike});});
  },[state.per_strike,state.spot]);

  var sliderMinutes=useMemo(function(){return history.map(function(s){return s.time_hhmm;});},[history]);
  var sliderIdx=sliderT?Math.max(0,sliderMinutes.indexOf(sliderT)):sliderMinutes.length;
  var maxSlider=sliderMinutes.length;

  var agg=state.aggregates||{};
  var net=agg.net_gex;
  var netColor=net>0?'#22c55e':net<0?'#ef4444':'#9ca3af';
  var wm=state.walls_migration||{};
  var tm=state.top_movers||{};
  var tb=tm.top_builder;
  var tu=tm.top_unwinder;
  var vrpVal=vrp.vrp;
  var vrpColor=vrp.regime==='RICH'?'#22c55e':vrp.regime==='CHEAP'?'#ef4444':'#9ca3af';

  var cardStyle={background:'#0a0f1c',border:'1px solid #1f2937',borderRadius:6,padding:'6px 8px'};

  return (
    <div style={{padding:10,color:'#e5e7eb'}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
        <span style={{fontSize:15,fontWeight:800}}>📊 GEX</span>
        <span style={{fontSize:9,color:'#9ca3af'}}>🕐 {state.last_compute_time||'--'} <span style={{width:6,height:6,borderRadius:'50%',background:sliderT?'#9ca3af':'#ef4444',display:'inline-block',marginLeft:4}}></span></span>
      </div>

      {/* HEADER STRIP — 4×2 grid: walls + key metrics */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:5,marginBottom:8}}>
        <div style={cardStyle}>
          <div style={{fontSize:8,color:'#6b7280',fontWeight:700}}>CALL WALL</div>
          <div style={{fontSize:12,fontWeight:800,color:'#22c55e'}}>{(wm.call_wall&&wm.call_wall.current)||agg.call_wall||'--'} <MigMini mig={wm.call_wall}/></div>
        </div>
        <div style={cardStyle}>
          <div style={{fontSize:8,color:'#6b7280',fontWeight:700}}>PUT FLOOR</div>
          <div style={{fontSize:12,fontWeight:800,color:'#ef4444'}}>{(wm.put_wall&&wm.put_wall.current)||agg.put_wall||'--'} <MigMini mig={wm.put_wall}/></div>
        </div>
        <div style={cardStyle}>
          <div style={{fontSize:8,color:'#6b7280',fontWeight:700}}>GEX PIN</div>
          <div style={{fontSize:12,fontWeight:800,color:'#22d3ee'}}>{(wm.gamma_flip&&wm.gamma_flip.current)||agg.gamma_flip||'--'} <MigMini mig={wm.gamma_flip}/></div>
        </div>
        <div style={cardStyle}>
          <div style={{fontSize:8,color:'#6b7280',fontWeight:700}}>DEALER Δ</div>
          <div style={{fontSize:12,fontWeight:800,color:'#f472b6'}}>{state.dealer_delta!=null?fmtCr(state.dealer_delta):'--'}</div>
        </div>
        <div style={cardStyle}>
          <div style={{fontSize:8,color:'#6b7280',fontWeight:700}}>NET GEX</div>
          <div style={{fontSize:12,fontWeight:800,color:netColor}}>{fmtCr(net)}</div>
        </div>
        <div style={cardStyle}>
          <div style={{fontSize:8,color:'#6b7280',fontWeight:700}}>Σ |CHAIN GAMMA|</div>
          <div style={{fontSize:12,fontWeight:800}}>{fmtCr(state.abs_chain_gamma)}</div>
        </div>
        <div style={cardStyle}>
          <div style={{fontSize:8,color:'#6b7280',fontWeight:700}}>VOL RISK PREM</div>
          <div style={{fontSize:12,fontWeight:800,color:vrpColor}}>{vrpVal!=null?(vrpVal>=0?'+':'')+vrpVal.toFixed(2)+'%':'--'}</div>
        </div>
        <div style={cardStyle}>
          <div style={{fontSize:8,color:'#6b7280',fontWeight:700}}>ZERO GAMMA</div>
          <div style={{fontSize:12,fontWeight:800,color:'#fb923c'}}>{agg.net_gex_cross||'--'}</div>
        </div>
      </div>

      {/* Flow Headline */}
      {(tb||tu)&&<div style={{...cardStyle,marginBottom:8}}>
        <div style={{display:'flex',justifyContent:'space-between',fontSize:10,marginBottom:3}}>
          {tb?<span style={{color:'#22c55e',fontWeight:700}}>🟢 {tb.strike}{tb.side==='ce'?'C':'P'} {fmtSignedK(tb.oi_delta)}</span>:<span/>}
          {tu?<span style={{color:'#fb923c',fontWeight:700}}>🔴 {tu.strike}{tu.side==='ce'?'C':'P'} {fmtSignedK(tu.oi_delta)}</span>:<span/>}
        </div>
        {state.narrative?<div style={{fontSize:9,color:'#9ca3af',lineHeight:1.4}}>{state.narrative}</div>:null}
      </div>}

      {/* Tab toggle */}
      <div style={{display:'flex',gap:4,marginBottom:8}}>
        <button onClick={function(){setTab('chart');}} style={{flex:1,padding:'6px 0',fontSize:11,fontWeight:700,background:tab==='chart'?'#1e3a8a':'#0a0f1c',color:tab==='chart'?'#93c5fd':'#9ca3af',border:'1px solid '+(tab==='chart'?'#3b82f6':'#1f2937'),borderRadius:4}}>CHART</button>
        <button onClick={function(){setTab('flow');}} style={{flex:1,padding:'6px 0',fontSize:11,fontWeight:700,background:tab==='flow'?'#1e3a8a':'#0a0f1c',color:tab==='flow'?'#93c5fd':'#9ca3af',border:'1px solid '+(tab==='flow'?'#3b82f6':'#1f2937'),borderRadius:4}}>Δ FLOW</button>
      </div>

      {tab==='chart'&&<>
      {/* Settings */}
      <div style={{background:'#0a0f1c',border:'1px solid #1f2937',borderRadius:8,padding:10,marginBottom:10}}>
        <div style={{display:'flex',gap:8,alignItems:'center',marginBottom:8}}>
          <span style={{fontSize:10,color:'#9ca3af',fontWeight:600}}>Expiry</span>
          <select value={state.expiry_choice||'current_week'} onChange={function(e){setExpiry(e.target.value);}} style={{flex:1,background:'#0a0f1c',border:'1px solid #1f2937',color:'#e5e7eb',padding:'4px 6px',borderRadius:4,fontSize:11}}>
            <option value="current_week">Current</option>
            <option value="next_week">Next week</option>
            <option value="month_end">Month end</option>
          </select>
        </div>
        <div style={{display:'flex',gap:4}}>
          {[null,5,10,20].map(function(n){
            var label=n===null?'All':String(n);
            var active=strikeRange===n;
            return <button key={label} onClick={function(){setStrikeRange(n);}} style={{
              flex:1,padding:'4px 0',fontSize:10,fontWeight:700,
              background:active?'#1e3a8a':'#0a0f1c', color:active?'#93c5fd':'#9ca3af',
              border:'1px solid '+(active?'#3b82f6':'#1f2937'), borderRadius:4
            }}>{label}</button>;
          })}
        </div>
      </div>

      {/* Chart */}
      <div style={{background:'#0a0f1c',border:'1px solid #1f2937',borderRadius:8,padding:8,height:340}}>
        <ResponsiveContainer>
          <ComposedChart data={chartData} margin={{top:6,right:6,left:0,bottom:20}}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1f2937"/>
            <XAxis dataKey="strike" stroke="#9ca3af" fontSize={8}/>
            <YAxis stroke="#9ca3af" fontSize={8}/>
            <Tooltip contentStyle={{background:'#0a0f1c',border:'1px solid #1f2937',borderRadius:6,fontSize:10}} formatter={function(v,n){return [v.toFixed(2)+' Cr',n];}}/>
            <Bar dataKey="net_gex" isAnimationActive={false}>
              {chartData.map(function(d,i){return <Cell key={i} fill={d.net_gex>=0?'#16a34a':'#b91c1c'}/>;})}
            </Bar>
            <Line type="monotone" dataKey="abs_gex" stroke="#3b82f6" strokeWidth={1.5} dot={false} isAnimationActive={false}/>
            {state.spot&&<ReferenceLine x={Math.round(state.spot/50)*50} stroke="#ffffff" strokeDasharray="3 3"/>}
            {agg.gamma_flip&&<ReferenceLine x={agg.gamma_flip} stroke="#22d3ee" strokeDasharray="3 3"/>}
            {agg.call_wall&&<ReferenceLine x={agg.call_wall} stroke="#22c55e" strokeDasharray="3 3"/>}
            {agg.put_wall&&<ReferenceLine x={agg.put_wall} stroke="#ef4444" strokeDasharray="3 3"/>}
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div style={{marginTop:8,display:'flex',alignItems:'center',gap:6,background:'#0a0f1c',border:'1px solid #1f2937',borderRadius:6,padding:'6px 8px'}}>
        <button onClick={function(){setSliderT(null);}} style={{background:'transparent',border:'1px solid #1f2937',color:'#9ca3af',padding:'2px 6px',fontSize:9,borderRadius:3}}>Live</button>
        <input type="range" min={0} max={maxSlider} value={sliderIdx} onChange={function(e){
          var i=+e.target.value;
          if(i>=maxSlider) setSliderT(null);
          else setSliderT(sliderMinutes[i]);
        }} style={{flex:1}}/>
        <span style={{fontSize:9,color:'#9ca3af',minWidth:36}}>{sliderT||sliderMinutes[sliderMinutes.length-1]||'--'}</span>
      </div>

      <div style={{background:'#0a0f1c',border:'1px solid #1f2937',borderRadius:8,padding:10,marginTop:10}}>
        <div style={{fontSize:10,fontWeight:700,color:'#fbbf24',marginBottom:4}}>🧭 Interpretation</div>
        <div style={{fontSize:11,color:'#cbd5e1',lineHeight:1.5}}>{state.interpretation||'Waiting for live data — market closed or no chain available yet.'}</div>
      </div>
      </>}

      {tab==='flow'&&<div style={{background:'#0a0f1c',border:'1px solid #1f2937',borderRadius:8,padding:8,overflowX:'auto'}}>
        <div style={{fontSize:9,color:'#6b7280',marginBottom:6,textAlign:'center'}}>Δ since {state.session_open_time||'open'}</div>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:9}}>
          <thead>
            <tr style={{color:'#6b7280',borderBottom:'1px solid #1f2937'}}>
              <th colSpan="3" style={{padding:'4px 0',color:'#22c55e'}}>CALLS</th>
              <th style={{padding:'4px 0',color:'#e5e7eb'}}>K</th>
              <th colSpan="3" style={{padding:'4px 0',color:'#ef4444'}}>PUTS</th>
            </tr>
            <tr style={{color:'#9ca3af',borderBottom:'1px solid #1f2937'}}>
              <th style={{padding:'3px 4px'}}>L</th>
              <th style={{padding:'3px 4px',textAlign:'right'}}>ΔOI</th>
              <th style={{padding:'3px 4px',textAlign:'right'}}>ΔIV</th>
              <th style={{padding:'3px 4px'}}></th>
              <th style={{padding:'3px 4px',textAlign:'right'}}>ΔIV</th>
              <th style={{padding:'3px 4px',textAlign:'right'}}>ΔOI</th>
              <th style={{padding:'3px 4px'}}>L</th>
            </tr>
          </thead>
          <tbody>
            {flowRows.length===0?<tr><td colSpan="7" style={{padding:14,textAlign:'center',color:'#6b7280'}}>Waiting...</td></tr>:flowRows.map(function(r){
              var cm=labelMeta(r.ce_label);
              var pm=labelMeta(r.pe_label);
              return <tr key={r.strike} style={{borderBottom:'1px solid #111827',background:r._atm?'rgba(59,130,246,0.08)':'transparent'}}>
                <td style={{padding:'3px 4px',color:cm.fg,fontWeight:700}}>{cm.txt}</td>
                <td style={{padding:'3px 4px',textAlign:'right',color:r.ce_oi_delta>0?'#22c55e':r.ce_oi_delta<0?'#fb923c':'#6b7280',fontWeight:700}}>{fmtSignedK(r.ce_oi_delta)}</td>
                <td style={{padding:'3px 4px',textAlign:'right',color:r.ce_iv_delta>0?'#22c55e':r.ce_iv_delta<0?'#ef4444':'#6b7280'}}>{r.ce_iv_delta?(r.ce_iv_delta>=0?'+':'')+r.ce_iv_delta.toFixed(1):'·'}</td>
                <td style={{padding:'3px 4px',textAlign:'center',color:r._atm?'#93c5fd':'#e5e7eb',fontWeight:r._atm?800:700}}>{r.strike}</td>
                <td style={{padding:'3px 4px',textAlign:'right',color:r.pe_iv_delta>0?'#22c55e':r.pe_iv_delta<0?'#ef4444':'#6b7280'}}>{r.pe_iv_delta?(r.pe_iv_delta>=0?'+':'')+r.pe_iv_delta.toFixed(1):'·'}</td>
                <td style={{padding:'3px 4px',textAlign:'right',color:r.pe_oi_delta>0?'#22c55e':r.pe_oi_delta<0?'#fb923c':'#6b7280',fontWeight:700}}>{fmtSignedK(r.pe_oi_delta)}</td>
                <td style={{padding:'3px 4px',color:pm.fg,fontWeight:700}}>{pm.txt}</td>
              </tr>;
            })}
          </tbody>
        </table>
        <div style={{marginTop:6,fontSize:8,color:'#4b5563',textAlign:'center'}}>B=build · B★=strong · U=unwnd · C=churn · ·=neutral</div>
      </div>}
    </div>
  );
}

export default GammaExposureM;
